import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { IMAGES, JAR_SIZES } from "@/lib/constants";
import {
  ArrowLeft, Heart, HeartOff, Trash2, ShoppingBag, ChefHat,
  Package, Clock, Loader2, Plus,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Tabs = ({ active, onChange }) => {
  const tabs = [
    { id: "recipes", label: "Saved Recipes", icon: ChefHat },
    { id: "orders", label: "Orders", icon: Package },
  ];
  return (
    <div className="flex gap-1 overflow-x-auto rounded-full bg-cream-secondary p-1 no-scrollbar" data-testid="kitchen-tabs">
      {tabs.map((tab) => (
        <button key={tab.id} onClick={() => onChange(tab.id)} data-testid={`kitchen-tab-${tab.id}`}
          className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-all duration-300 sm:px-5 ${active === tab.id ? "bg-white text-sw-text shadow-sm" : "text-sw-text-muted hover:text-sw-text"}`}>
          <tab.icon size={15} />
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export default function MyKitchenPage() {
  const navigate = useNavigate();
  const { user, authHeaders, fetchCartCount, setCartCount } = useAuth();
  const [tab, setTab] = useState("recipes");
  const [recipes, setRecipes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "My Kitchen | Swadishes";
    let isMounted = true;
    
    const load = async () => {
      setLoading(true);
      const headers = authHeaders();
      try {
        const [recipesRes, ordersRes] = await Promise.all([
          fetch(`${API}/api/recipes`, { headers }),
          fetch(`${API}/api/orders`, { headers })
        ]);
        if (isMounted) {
          if (recipesRes.ok) setRecipes(await recipesRes.json());
          if (ordersRes.ok) setOrders(await ordersRes.json());
        }
      } catch (err) {
        console.error("Error loading data:", err);
      }
      if (isMounted) setLoading(false);
    };
    load();
    
    return () => { 
      isMounted = false;
      document.title = "Swadishes"; 
    };
  }, [authHeaders]);

  const toggleFav = async (id) => {
    const headers = authHeaders();
    await fetch(`${API}/api/recipes/${id}/favorite`, { method: "PUT", headers });
    setRecipes((prev) => prev.map((r) => r.id === id ? { ...r, is_favorite: !r.is_favorite } : r));
  };

  const deleteRecipe = async (id) => {
    const headers = authHeaders();
    await fetch(`${API}/api/recipes/${id}`, { method: "DELETE", headers });
    setRecipes((prev) => prev.filter((r) => r.id !== id));
  };

  const addToCart = async (recipe) => {
    const headers = authHeaders();
    const res = await fetch(`${API}/api/recipes/${recipe.id}/to-cart?jar_size=regular`, {
      method: "POST", headers,
    });
    if (res.ok) {
      const data = await res.json();
      setCartCount(data.cart_count);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-earth-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="my-kitchen-page">
      <header className="fixed top-0 w-full z-50 bg-cream/90 backdrop-blur-xl border-b border-gray-100/50">
        <div className="section-container flex items-center justify-between h-14 lg:h-16">
          <button onClick={() => navigate("/")} data-testid="kitchen-back-btn" className="flex items-center gap-2 text-sw-text-secondary hover:text-earth-green transition-colors">
            <ArrowLeft size={18} />
            <span className="text-sm font-medium hidden sm:block">Home</span>
          </button>
          <div className="flex items-center gap-2">
            <img src={IMAGES.logo} alt="Swadishes" className="h-7 lg:h-9 rounded-md" />
            <span className="text-sm lg:text-base font-heading font-700 text-sw-text">My Kitchen</span>
          </div>
          <button onClick={() => navigate("/cart")} data-testid="kitchen-cart-btn" className="flex items-center gap-1 text-sm text-sw-text-secondary hover:text-earth-green transition-colors">
            <ShoppingBag size={18} />
          </button>
        </div>
      </header>

      <main className="pt-20 lg:pt-24 pb-16">
        <div className="section-container">
          <div className="mb-6">
            <h1 className="text-2xl lg:text-3xl font-heading font-800 text-sw-text">
              Hello, {user?.name || "Chef"}
            </h1>
            <p className="text-sm text-sw-text-muted mt-1">Your personal kitchen space</p>
          </div>

          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Tabs active={tab} onChange={setTab} />
            <button onClick={() => navigate("/studio")} data-testid="kitchen-new-recipe-btn"
              className="hidden sm:flex items-center gap-2 bg-earth-green text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-earth-green-dark transition-all">
              <Plus size={16} /> New Recipe
            </button>
          </div>

          <AnimatePresence mode="wait">
            {tab === "recipes" && (
              <motion.div key="recipes" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                {recipes.length === 0 ? (
                  <div className="text-center py-20 bg-white rounded-3xl border border-gray-100" data-testid="empty-recipes">
                    <ChefHat size={40} className="text-sw-text-muted/30 mx-auto mb-4" />
                    <p className="text-sw-text-muted mb-4">No saved recipes yet</p>
                    <button onClick={() => navigate("/studio")} className="bg-earth-green text-white rounded-full px-6 py-2.5 text-sm font-medium">
                      Create Your First Recipe
                    </button>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="recipes-grid">
                    {recipes.map((recipe) => (
                      <motion.div key={recipe.id} layout className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`recipe-card-${recipe.id}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-heading font-700 text-sw-text text-base">{recipe.pickle_name}</h3>
                            <p className="text-xs text-sw-text-muted mt-0.5">
                              <Clock size={10} className="inline mr-1" />
                              {new Date(recipe.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <button onClick={() => toggleFav(recipe.id)} data-testid={`recipe-fav-${recipe.id}`}
                            className={`p-1.5 rounded-full transition-colors ${recipe.is_favorite ? "text-spice-mango" : "text-sw-text-muted hover:text-spice-mango"}`}>
                            {recipe.is_favorite ? <Heart size={16} className="fill-current" /> : <HeartOff size={16} />}
                          </button>
                        </div>
                        {recipe.notes && <p className="text-xs text-sw-text-secondary mb-3 line-clamp-2">{recipe.notes}</p>}
                        <div className="flex flex-wrap gap-1 mb-4">
                          {recipe.pickle_config?.base && <span className="text-[10px] bg-cream-secondary text-sw-text-secondary px-2 py-0.5 rounded-full">{recipe.pickle_config.base}</span>}
                          {recipe.pickle_config?.style && <span className="text-[10px] bg-cream-secondary text-sw-text-secondary px-2 py-0.5 rounded-full">{recipe.pickle_config.style}</span>}
                        </div>
                        <div className="flex gap-2 pt-3 border-t border-gray-100">
                          <button onClick={() => addToCart(recipe)} data-testid={`recipe-to-cart-${recipe.id}`}
                            className="flex-1 bg-earth-green text-white rounded-full py-2 text-xs font-medium hover:bg-earth-green-dark transition-all flex items-center justify-center gap-1">
                            <ShoppingBag size={12} /> Add to Cart
                          </button>
                          <button onClick={() => deleteRecipe(recipe.id)} data-testid={`recipe-delete-${recipe.id}`}
                            className="p-2 rounded-full text-sw-text-muted hover:text-red-500 hover:bg-red-50 transition-all">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {tab === "orders" && (
              <motion.div key="orders" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                {orders.length === 0 ? (
                  <div className="text-center py-20 bg-white rounded-3xl border border-gray-100" data-testid="empty-orders">
                    <Package size={40} className="text-sw-text-muted/30 mx-auto mb-4" />
                    <p className="text-sw-text-muted mb-4">No orders yet</p>
                    <button onClick={() => navigate("/studio")} className="bg-earth-green text-white rounded-full px-6 py-2.5 text-sm font-medium">
                      Start Creating
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="orders-list">
                    {orders.map((order) => (
                      <div key={order.id} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm" data-testid={`order-card-${order.id}`}>
                        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="text-xs text-sw-text-muted">Order #{order.id.slice(0, 8)}</p>
                            <p className="text-xs text-sw-text-muted">{new Date(order.created_at).toLocaleDateString()}</p>
                          </div>
                          <span className={`text-xs font-medium px-3 py-1 rounded-full ${order.status === "confirmed" ? "bg-earth-green/10 text-earth-green" : "bg-cream-secondary text-sw-text-muted"}`}>
                            {order.status}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {order.items?.map((item, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span className="text-sw-text-secondary">{item.pickle_name || "Custom Pickle"}</span>
                              <span className="font-medium text-sw-text">&#8377;{item.price}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between mt-3 pt-3 border-t border-gray-100">
                          <span className="text-sm font-medium text-sw-text">Total</span>
                          <span className="text-base font-heading font-700 text-earth-green">&#8377;{order.total}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Mobile FAB */}
      <button onClick={() => navigate("/studio")} data-testid="kitchen-fab"
        className="sm:hidden fixed bottom-6 right-6 z-40 bg-earth-green text-white w-14 h-14 rounded-full shadow-lg shadow-earth-green/30 flex items-center justify-center">
        <Plus size={24} />
      </button>
    </div>
  );
}
